import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { describeGenericApiError, extractApiErrorMessage } from '../../core/http/api-error.util';
import { TaskItem } from './task-item.model';
import { TaskItemService } from './task-item.service';

@Component({
  selector: 'app-task-items',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './task-items.component.html',
  styleUrls: ['./task-items.component.scss']
})
export class TaskItemsComponent implements OnChanges {
  @Input({ required: true }) taskId = '';

  private readonly taskItemService = inject(TaskItemService);

  readonly taskItems = signal<TaskItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly newTaskItemText = signal('');
  readonly draftById = signal<Record<string, string>>({});
  readonly submittingById = signal<Record<string, boolean>>({});
  readonly adding = signal(false);
  readonly clearingDone = signal(false);

  readonly sortedTaskItems = computed(() => [...this.taskItems()].sort((left, right) => {
    if (left.done !== right.done) {
      return left.done ? 1 : -1;
    }
    return left.createdAt.localeCompare(right.createdAt);
  }));
  readonly hasDone = computed(() => this.taskItems().some((taskItem) => taskItem.done));

  ngOnChanges(changes: SimpleChanges): void {
    if ('taskId' in changes) {
      this.load();
    }
  }

  load(): void {
    const taskId = this.taskId.trim();
    if (!taskId) {
      this.taskItems.set([]);
      this.draftById.set({});
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.taskItemService.list(taskId).subscribe({
      next: (taskItems) => {
        this.taskItems.set(taskItems);
        this.draftById.set(Object.fromEntries(taskItems.map((taskItem) => [taskItem.id, taskItem.text])));
        this.loading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load task items'));
        this.loading.set(false);
      }
    });
  }

  setNewTaskItemText(value: string): void {
    this.newTaskItemText.set(value);
  }

  getDraft(taskItemId: string): string {
    return this.draftById()[taskItemId] ?? '';
  }

  setDraft(taskItemId: string, value: string): void {
    this.draftById.update((current) => ({ ...current, [taskItemId]: value }));
  }

  isSubmitting(taskItemId: string): boolean {
    return !!this.submittingById()[taskItemId];
  }

  addTaskItem(): void {
    const taskId = this.taskId.trim();
    const text = this.newTaskItemText().trim();
    if (!taskId || !text || this.adding()) {
      return;
    }

    this.error.set(null);
    this.adding.set(true);
    const tempId = `temp-${Date.now()}`;
    const optimisticTaskItem: TaskItem = {
      id: tempId,
      taskId,
      text,
      done: false,
      createdAt: new Date().toISOString(),
      completedAt: null
    };
    const previousTaskItems = this.taskItems();
    this.taskItems.set([...previousTaskItems, optimisticTaskItem]);
    this.draftById.update((current) => ({ ...current, [tempId]: text }));
    this.newTaskItemText.set('');

    this.taskItemService.create(taskId, { text }).subscribe({
      next: (created) => {
        this.taskItems.update((current) => current.map((taskItem) => taskItem.id === tempId ? created : taskItem));
        this.draftById.update((current) => {
          const { [tempId]: _temp, ...rest } = current;
          return { ...rest, [created.id]: created.text };
        });
        this.adding.set(false);
      },
      error: (err: unknown) => {
        this.taskItems.set(previousTaskItems);
        this.draftById.set(Object.fromEntries(previousTaskItems.map((taskItem) => [taskItem.id, taskItem.text])));
        this.error.set(extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Failed to create task item'));
        this.adding.set(false);
      }
    });
  }

  toggleDone(taskItemId: string, done: boolean): void {
    const taskId = this.taskId.trim();
    if (!taskId || this.isSubmitting(taskItemId)) {
      return;
    }

    this.error.set(null);
    const previousTaskItems = this.taskItems();
    const completedAt = done ? new Date().toISOString() : null;
    this.taskItems.update((current) => current.map((taskItem) => taskItem.id === taskItemId
      ? { ...taskItem, done, completedAt }
      : taskItem));
    this.setSubmitting(taskItemId, true);

    this.taskItemService.update(taskId, taskItemId, { done }).subscribe({
      next: (updated) => {
        this.taskItems.update((current) => current.map((taskItem) => taskItem.id === taskItemId ? updated : taskItem));
        this.draftById.update((current) => ({ ...current, [taskItemId]: updated.text }));
        this.setSubmitting(taskItemId, false);
      },
      error: (err: unknown) => {
        this.taskItems.set(previousTaskItems);
        this.error.set(extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Failed to update task item'));
        this.setSubmitting(taskItemId, false);
      }
    });
  }

  saveText(taskItem: TaskItem): void {
    const taskId = this.taskId.trim();
    if (!taskId || this.isSubmitting(taskItem.id)) {
      return;
    }
    const nextText = this.getDraft(taskItem.id).trim();
    if (!nextText || nextText === taskItem.text) {
      this.setDraft(taskItem.id, taskItem.text);
      return;
    }

    this.error.set(null);
    const previousTaskItems = this.taskItems();
    this.taskItems.update((current) => current.map((entry) => entry.id === taskItem.id ? { ...entry, text: nextText } : entry));
    this.setSubmitting(taskItem.id, true);

    this.taskItemService.update(taskId, taskItem.id, { text: nextText }).subscribe({
      next: (updated) => {
        this.taskItems.update((current) => current.map((entry) => entry.id === taskItem.id ? updated : entry));
        this.draftById.update((current) => ({ ...current, [taskItem.id]: updated.text }));
        this.setSubmitting(taskItem.id, false);
      },
      error: (err: unknown) => {
        this.taskItems.set(previousTaskItems);
        this.draftById.update((current) => ({ ...current, [taskItem.id]: taskItem.text }));
        this.error.set(extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Failed to save task item'));
        this.setSubmitting(taskItem.id, false);
      }
    });
  }

  deleteTaskItem(taskItemId: string): void {
    const taskId = this.taskId.trim();
    if (!taskId || this.isSubmitting(taskItemId)) {
      return;
    }
    this.error.set(null);
    const previousTaskItems = this.taskItems();
    this.taskItems.set(previousTaskItems.filter((taskItem) => taskItem.id !== taskItemId));
    this.draftById.update((current) => {
      const { [taskItemId]: _removed, ...rest } = current;
      return rest;
    });
    this.setSubmitting(taskItemId, true);

    this.taskItemService.delete(taskId, taskItemId).subscribe({
      next: () => {
        this.setSubmitting(taskItemId, false);
      },
      error: (err: unknown) => {
        this.taskItems.set(previousTaskItems);
        this.draftById.set(Object.fromEntries(previousTaskItems.map((taskItem) => [taskItem.id, taskItem.text])));
        this.error.set(extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Failed to delete task item'));
        this.setSubmitting(taskItemId, false);
      }
    });
  }

  clearDoneTaskItems(): void {
    const taskId = this.taskId.trim();
    if (!taskId || this.clearingDone() || !this.hasDone()) {
      return;
    }
    this.error.set(null);
    this.clearingDone.set(true);
    const previousTaskItems = this.taskItems();
    const remainingTaskItems = previousTaskItems.filter((taskItem) => !taskItem.done);
    this.taskItems.set(remainingTaskItems);
    this.draftById.set(Object.fromEntries(remainingTaskItems.map((taskItem) => [taskItem.id, taskItem.text])));

    this.taskItemService.clearDone(taskId).subscribe({
      next: () => {
        this.clearingDone.set(false);
      },
      error: (err: unknown) => {
        this.taskItems.set(previousTaskItems);
        this.draftById.set(Object.fromEntries(previousTaskItems.map((taskItem) => [taskItem.id, taskItem.text])));
        this.error.set(extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Failed to clear completed items'));
        this.clearingDone.set(false);
      }
    });
  }

  private setSubmitting(taskItemId: string, value: boolean): void {
    this.submittingById.update((current) => ({ ...current, [taskItemId]: value }));
  }
}
